import type {
  LoomAuthoringDocument,
  LoomSceneNode,
} from "../features/parametric-geometry/loomAuthoringModel";
import { requireAttachedLoomMirrorPartners } from "../features/parametric-geometry/loomMirrorRelations";
import { parseLoomAuthoringDocument } from "../features/parametric-geometry/loomAuthoringModel";
import {
  loomAuthoringDocumentStateId,
  type LoomAuthoringHead,
} from "../features/parametric-geometry/loomAuthoringHistory";
import type {
  ThreadAeroEnvelope,
  ThreadAeroOperationResult,
  ThreadAeroTarget,
} from "./aeroWorkbench";
import { apiClient } from "./client";

export const LOOM_AUTHORING_DEFINITION_PATH = "/thread/v1/aero/parametric-geometries";

const REGISTERED_ID = /^[a-z][a-z0-9_.-]{0,99}$/;
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MAX_COMPONENTS = 256;
const MAX_REQUEST_BYTES = 8 * 1024 * 1024;
const MAX_JSON_DEPTH = 24;
const MAX_JSON_NODES = 200_000;

export type LoomRecordedJson =
  | null
  | boolean
  | number
  | string
  | readonly LoomRecordedJson[]
  | { readonly [key: string]: LoomRecordedJson };

export interface LoomRecordedComponent {
  component_id: string;
  parent_component_id?: string;
  kind: "loom_primitive" | "loom_transform" | "loom_boolean" | "loom_import_adapter";
  parameterization: "loom_authoring_v1";
  parameters: {
    node: LoomRecordedJson;
  };
  metadata: {
    declaration_status: "client_declared_unverified";
    definition_origin: "native_loom" | "import_adapter";
    loom_document: {
      schema: "loom-authoring/v1";
      document_id: string;
      authoring_state_id: string;
      node_index: number;
      root_node_ids: readonly string[];
    };
    adapter_provenance?: {
      adapter: "openvsp-vsp3";
      artifact_key: string;
      selected_component_ids: readonly string[];
    };
  };
}

export interface LoomRecordedDefinition {
  coordinate_frame: "aircraft_body_x_forward_y_right_z_up";
  length_unit: "m";
  source: { format: "native" };
  components: LoomRecordedComponent[];
}

export interface LoomAuthoringDefinitionRequest {
  project_id: string;
  idempotency_key: string;
  target: ThreadAeroTarget;
  definition: LoomRecordedDefinition;
}

export interface LoomAuthoringRecordInput {
  projectId: string;
  logicalId: string;
  targetName: string;
  document: LoomAuthoringDocument;
  head: LoomAuthoringHead;
}

export interface LoomAuthoringRecordIdentity {
  logicalId: string;
  targetName: string;
}

export interface LoomAuthoringRecordedContent {
  schema_version: "aero.parametric-geometry-definition.v1";
  declaration_status: "client_declared_unverified";
  definition: LoomRecordedDefinition;
}

export type LoomAuthoringRecordResult = ThreadAeroOperationResult<LoomAuthoringRecordedContent>;

function requireRegisteredId(value: string, path: string): string {
  if (!REGISTERED_ID.test(value)) {
    throw new Error(`${path} must be a lowercase registered-style name of at most 100 characters`);
  }
  return value;
}

function requireUuid(value: string, path: string): string {
  if (!CANONICAL_UUID.test(value)) {
    throw new Error(`${path} must be a canonical lowercase UUID`);
  }
  return value;
}

function requireTargetName(value: string): string {
  if (value !== value.trim() || value.length === 0 || value.length > 300) {
    throw new Error("targetName must be a trimmed string between 1 and 300 characters");
  }
  return value;
}

function validateNativeIds(document: LoomAuthoringDocument): void {
  requireAttachedLoomMirrorPartners(document);
  requireRegisteredId(document.documentId, "document.documentId");
  for (const [index, node] of document.nodes.entries()) {
    requireRegisteredId(node.id, `document.nodes[${index}].id`);
    if (node.type === "transform") {
      if (node.operation.transform === "mirror-y") throw new Error("legacy mirror-y has ambiguous cross-engine semantics and must be migrated before canonical V3 recording");
      requireRegisteredId(node.inputNodeId, `document.nodes[${index}].inputNodeId`);
    } else if (node.type === "boolean") {
      node.inputNodeIds.forEach((id, inputIndex) => {
        requireRegisteredId(id, `document.nodes[${index}].inputNodeIds[${inputIndex}]`);
      });
    } else if (node.type === "primitive" && node.definition.primitive === "wing") {
      node.definition.panels.forEach((panel, panelIndex) => {
        requireRegisteredId(panel.panelId, `document.nodes[${index}].definition.panels[${panelIndex}].panelId`);
      });
    } else if (node.type === "primitive" && node.definition.primitive === "fuselage") {
      node.definition.stations.forEach((station, stationIndex) => {
        requireRegisteredId(station.stationId, `document.nodes[${index}].definition.stations[${stationIndex}].stationId`);
      });
    }
  }
  document.rootNodeIds.forEach((id, index) => {
    requireRegisteredId(id, `document.rootNodeIds[${index}]`);
  });
}

function nodeKind(node: LoomSceneNode): LoomRecordedComponent["kind"] {
  if (node.type === "primitive") return "loom_primitive";
  if (node.type === "transform") return "loom_transform";
  if (node.type === "boolean") return "loom_boolean";
  return "loom_import_adapter";
}

function asRecordedJson(value: unknown): LoomRecordedJson {
  return value as LoomRecordedJson;
}

function mapComponent(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
  nodeIndex: number,
  stateId: string,
): LoomRecordedComponent {
  return {
    component_id: node.id,
    ...(node.type === "transform" ? { parent_component_id: node.inputNodeId } : {}),
    kind: nodeKind(node),
    parameterization: "loom_authoring_v1",
    parameters: { node: asRecordedJson(node) },
    metadata: {
      declaration_status: "client_declared_unverified",
      definition_origin: node.type === "import" ? "import_adapter" : "native_loom",
      loom_document: {
        schema: document.schema,
        document_id: document.documentId,
        authoring_state_id: stateId,
        node_index: nodeIndex,
        root_node_ids: [...document.rootNodeIds],
      },
      ...(node.type === "import" ? {
        adapter_provenance: {
          adapter: node.adapter,
          artifact_key: node.artifactKey,
          selected_component_ids: [...node.selectedComponentIds],
        },
      } : {}),
    },
  };
}

function parseStoredRecordIdentity(raw: string): LoomAuthoringRecordIdentity {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw) as unknown;
  } catch {
    throw new Error("stored Loom record identity is malformed JSON");
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("stored Loom record identity must be an object");
  }
  const item = parsed as Record<string, unknown>;
  if (Object.keys(item).sort().join(",") !== "logicalId,targetName"
    || typeof item.logicalId !== "string" || typeof item.targetName !== "string") {
    throw new Error("stored Loom record identity has an invalid shape");
  }
  return {
    logicalId: requireUuid(item.logicalId, "stored logicalId"),
    targetName: requireTargetName(item.targetName),
  };
}

function readRecordIdentityStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    throw new Error("Loom record identity persistence is unavailable");
  }
}

export async function getOrCreateLoomAuthoringRecordIdentity(
  projectId: string,
  targetName: string,
): Promise<LoomAuthoringRecordIdentity> {
  requireUuid(projectId, "projectId");
  requireTargetName(targetName);
  const identityKey = `thread:loom:record-identity:${projectId}`;
  const legacyKey = `thread:loom:logical-id:${projectId}`;
  if (typeof window === "undefined") {
    throw new Error("Loom record identity requires browser persistence");
  }
  if (typeof navigator === "undefined" || !navigator.locks) {
    throw new Error("Loom record identity Web Locks coordination is unavailable");
  }
  return navigator.locks.request(identityKey, { mode: "exclusive" }, () => {
    let storage: Storage;
    try {
      storage = window.localStorage;
    } catch {
      throw new Error("Loom record identity persistence is unavailable");
    }
    const raw = readRecordIdentityStorage(storage, identityKey);
    if (raw !== null) return parseStoredRecordIdentity(raw);
    const legacy = readRecordIdentityStorage(storage, legacyKey);
    const legacyLogicalId = legacy && CANONICAL_UUID.test(legacy) ? legacy : null;
    if (!legacyLogicalId && (typeof crypto === "undefined" || typeof crypto.randomUUID !== "function")) {
      throw new Error("Secure UUID generation is unavailable in this browser");
    }
    const identity = { logicalId: legacyLogicalId ?? crypto.randomUUID(), targetName };
    const encoded = JSON.stringify(identity);
    const compared = readRecordIdentityStorage(storage, identityKey);
    if (compared !== raw) {
      if (compared !== null) return parseStoredRecordIdentity(compared);
      throw new Error("Loom record identity changed during compare-and-set");
    }
    try {
      storage.setItem(identityKey, encoded);
    } catch {
      throw new Error("Loom record identity persistence is unavailable");
    }
    const committed = readRecordIdentityStorage(storage, identityKey);
    if (committed !== encoded) {
      throw new Error("Loom record identity compare-and-set was not committed");
    }
    return identity;
  });
}

function rejectUnrecordableJson(value: unknown): void {
  const pending: Array<{ value: unknown; depth: number; path: string }> = [{ value, depth: 1, path: "$" }];
  let count = 0;
  while (pending.length) {
    const item = pending.pop()!;
    count += 1;
    if (count > MAX_JSON_NODES) throw new Error(`request exceeds the ${MAX_JSON_NODES} JSON-node limit`);
    if (item.depth > MAX_JSON_DEPTH) throw new Error(`request exceeds the ${MAX_JSON_DEPTH}-level JSON-depth limit`);
    if (typeof item.value === "number") {
      if (!Number.isFinite(item.value) || (Number.isInteger(item.value) && !Number.isSafeInteger(item.value))) {
        throw new Error(`${item.path} is not RFC 8785/I-JSON recordable`);
      }
      continue;
    }
    if (typeof item.value === "string") {
      for (let index = 0; index < item.value.length; index += 1) {
        const code = item.value.charCodeAt(index);
        if (code >= 0xd800 && code <= 0xdbff) {
          const next = item.value.charCodeAt(index + 1);
          if (!(next >= 0xdc00 && next <= 0xdfff)) throw new Error(`${item.path} contains an unpaired Unicode surrogate`);
          index += 1;
        } else if (code >= 0xdc00 && code <= 0xdfff) {
          throw new Error(`${item.path} contains an unpaired Unicode surrogate`);
        }
      }
      continue;
    }
    if (item.value === null || typeof item.value === "boolean") continue;
    if (Array.isArray(item.value)) {
      item.value.forEach((entry, index) => pending.push({ value: entry, depth: item.depth + 1, path: `${item.path}[${index}]` }));
      continue;
    }
    if (typeof item.value === "object") {
      for (const [key, entry] of Object.entries(item.value)) {
        pending.push({ value: key, depth: item.depth + 1, path: `${item.path} key` });
        pending.push({ value: entry, depth: item.depth + 1, path: `${item.path}.${key}` });
      }
      continue;
    }
    throw new Error(`${item.path} is not JSON-recordable`);
  }
}

export function buildLoomAuthoringDefinitionRequest({
  document: rawDocument,
  head,
  logicalId,
  projectId,
  targetName,
}: LoomAuthoringRecordInput): LoomAuthoringDefinitionRequest {
  const document = parseLoomAuthoringDocument(rawDocument);
  requireUuid(projectId, "projectId");
  requireUuid(logicalId, "logicalId");
  requireTargetName(targetName);
  validateNativeIds(document);
  if (document.nodes.length === 0) throw new Error("document.nodes must contain at least one recordable node");
  if (document.nodes.length > MAX_COMPONENTS) throw new Error(`document.nodes exceeds the ${MAX_COMPONENTS}-component limit`);
  if (!Number.isSafeInteger(document.revision)) throw new Error("document.revision is outside the I-JSON safe integer range");
  if (!head || typeof head !== "object" || !Number.isSafeInteger(head.revision)) {
    throw new Error("head must contain a safe integer revision and immutable stateId");
  }
  if (head.revision !== document.revision) throw new Error("head.revision must equal document.revision");
  const stateId = loomAuthoringDocumentStateId(document);
  if (head.stateId !== stateId) throw new Error("head.stateId does not match document content");

  const request: LoomAuthoringDefinitionRequest = {
    project_id: projectId,
    idempotency_key: `loom-authoring:${document.documentId}:${logicalId}:${stateId}`,
    target: {
      logical_id: logicalId,
      name: targetName,
      metadata: {
        authoring_system: "loom",
        authoring_schema: document.schema,
        authoring_document_id: document.documentId,
        authoring_state_id: stateId,
      },
    },
    definition: {
      coordinate_frame: "aircraft_body_x_forward_y_right_z_up",
      length_unit: "m",
      source: { format: "native" },
      components: document.nodes.map((node, index) => mapComponent(document, node, index, stateId)),
    },
  };
  rejectUnrecordableJson(request);
  const byteLength = new TextEncoder().encode(JSON.stringify(request)).byteLength;
  if (byteLength > MAX_REQUEST_BYTES) throw new Error(`request exceeds the ${MAX_REQUEST_BYTES}-byte definition limit`);
  return request;
}

export async function recordLoomAuthoringDefinition(
  input: LoomAuthoringRecordInput,
  signal?: AbortSignal,
): Promise<LoomAuthoringRecordResult> {
  const body = buildLoomAuthoringDefinitionRequest(input);
  const response = await apiClient<ThreadAeroEnvelope<LoomAuthoringRecordedContent>>(
    LOOM_AUTHORING_DEFINITION_PATH,
    { method: "POST", body, ...(signal ? { signal } : {}) },
  );
  return response.data;
}
